import moment from "moment";

export default class sortHelper {
	/**
     * Sorts an array of objects alphabetically based on a specified key.
     * @param list - The array of objects to sort.
     * @param key - The key in the objects to sort by.
     * @param order - If true, sorts in ascending order; if false, sorts in descending order.
     * @returns The sorted array of objects.
     */
	public static sortAlphabetically(list: any[], key: string, order: boolean): any[] {
		return list
			.sort((a, b) => {
				const nameA = a[key] == null ? "" : a[key].toLowerCase();
				const nameB = b[key] == null ? "" : b[key].toLowerCase();

				if (!nameA && nameB) {
					return 1;
				}
				if (nameA && !nameB) {
					return -1;
				}

				if (order) {
					return nameA.localeCompare(nameB);
				}

				return nameB.localeCompare(nameA);
			});
	}

	/**
     * Sorts an array of objects alphabetically in an accent-insensitive way, based on a specified key.
     * @param list - The array of objects to sort.
     * @param key - The key in the object to sort by.
     * @param order - true for ascending sort, false for descending sort.
     * @returns The array sorted in an accent-insensitive way.
     */
	public static sortAccentInsensitive(list: any[], key: string, order: boolean): any[] {
		return list
			.sort((a, b) => {
				// Normalizes strings to remove accents and converts to lowercase
				const nameA = (a[key] || "" as string).toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
				const nameB = (b[key] || "" as string).toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");

				// Compares the normalized values according to the requested order
				if (nameA < nameB) {
					return (order ? -1 : 1);
				}
				if (nameA > nameB) {
					return (order ? 1 : -1);
				}

				return 0;
			});
	}

	/**
	 * Sorts an array of objects by a date key in ascending or descending order.
	 * @param key
	 * @param order
	 * @param list
	 * @returns The sorted array of objects.
	 */
	public static sortByDateWithKey<T extends Record<string, any>>(key: string, order: "ascending" | "descending", list: T[]): T[] {
		return list.sort((a, b) => {
			const dateA = moment(a[key], "DD/MM/YYYY").toDate().getTime();
			const dateB = moment(b[key], "DD/MM/YYYY").toDate().getTime();

			return order === "ascending" ? dateA - dateB : dateB - dateA;
		});
	}

	/**
	 * Sorts an array of objects by a numeric key in ascending or descending order.
	 * @param key
	 * @param order
	 * @param list
	 * @returns The sorted array of objects.
	 */
	public static sortByNumberWithKey<T extends Record<string, any>>(key: string, order: "ascending" | "descending", list: T[]): T[] {
		return list.sort((a, b) => {
			return order === "ascending" ? Number(a[key]) - Number(b[key]) : Number(b[key]) - Number(a[key]);
		});
	}

	/**
	 * Sorts an array of objects by a specified key in an antichronological order.
	 * @param key
	 * @param order
	 * @param list
	 * @returns The sorted array of objects.
	 */
	public static sortByKeyAnte<T extends Record<string, any>>(key: string, order: "ascending" | "descending", list: T[]): T[] {
		const absencesParType = list.reduce((acc, absence) => {
			(acc[absence[key]] = acc[absence[key]] || []).push(absence);

			return acc;
		}, {} as { [key: string]: T[] });

		// Trie chaque tableau d'absences par dateDebut antéchronologique
		for (const type in absencesParType) {
			absencesParType[type].sort((a, b) =>
				moment(b.dateDebut).valueOf() - moment(a.dateDebut).valueOf()
			);
		}

		const sortedKeys = Object.keys(absencesParType).sort((a, b) => {
			return order === "ascending"
				? a.localeCompare(b)
				: b.localeCompare(a);
		});

		return sortedKeys.reduce((acc: T[], type) => acc.concat(absencesParType[type]), []);
	}
}



// WEBPACK FOOTER //
// ./src/Helper/sortHelper.ts